import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Search, Trash2, Globe, Users, CheckCircle } from 'lucide-react';
import { useCampaigns, useAddDocument, useDeleteDocument } from '@/hooks/useFirestore';
import { cloudFunctions, logAnalyticsEvent } from '@/lib/firebase';
import { db } from '@/lib/firebase';
import { collection, query, where, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { GlassCard } from '@/components/ui/GlassCard';
import { Badge } from '@/components/ui/Badge';
import { HackerLoadingScreen } from '@/components/ui/HackerLoadingScreen';
import { QuotaLimitModal } from '@/components/ui/QuotaLimitModal';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/AlertDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { formatDate } from '@/lib/utils';
import { allCountries, browserCountry, countryFlag, countryName } from '@/lib/countries';

const COUNTRY_OPTIONS = allCountries();
// Preselect the visitor's own country when their browser locale states one.
const DEFAULT_CAMPAIGN_COUNTRY = browserCountry() ?? '';

const NICHES = [
  { value: 'any', label: 'Any Niche (Random)' },
  { value: 'plumber', label: 'Plumber' },
  { value: 'electrician', label: 'Electrician' },
  { value: 'hvac', label: 'HVAC / Air Conditioning' },
  { value: 'roofing', label: 'Roofing Contractor' },
  { value: 'landscaping', label: 'Landscaping' },
  { value: 'cleaning', label: 'Cleaning Service' },
  { value: 'painting', label: 'Painter' },
  { value: 'flooring', label: 'Flooring Contractor' },
  { value: 'pest_control', label: 'Pest Control' },
  { value: 'locksmith', label: 'Locksmith' },
  { value: 'garage_door', label: 'Garage Door Repair' },
  { value: 'pool_service', label: 'Pool Service' },
  { value: 'tree_service', label: 'Tree Service' },
  { value: 'moving', label: 'Moving Company' },
  { value: 'auto_repair', label: 'Auto Repair Shop' },
  { value: 'dentist', label: 'Dentist' },
  { value: 'chiropractor', label: 'Chiropractor' },
  { value: 'veterinarian', label: 'Veterinarian' },
  { value: 'salon', label: 'Hair Salon / Barber' },
  { value: 'spa', label: 'Spa / Massage' },
  { value: 'gym', label: 'Gym / Fitness Center' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'bakery', label: 'Bakery' },
  { value: 'photography', label: 'Photographer' },
  { value: 'real_estate', label: 'Real Estate Agent' },
  { value: 'accounting', label: 'Accountant / CPA' },
  { value: 'lawyer', label: 'Lawyer / Attorney' },
  { value: 'insurance', label: 'Insurance Agent' },
  { value: 'daycare', label: 'Daycare / Childcare' },
  { value: 'tutoring', label: 'Tutoring Service' },
  { value: 'pet_grooming', label: 'Pet Grooming' },
  { value: 'catering', label: 'Catering' },
  { value: 'florist', label: 'Florist' },
  { value: 'tailor', label: 'Tailor / Alterations' },
];

const MAX_LEADS_OPTIONS: Array<{ value: number | 'custom'; label: string }> = [
  { value: 10, label: '10 leads' },
  { value: 25, label: '25 leads' },
  { value: 50, label: '50 leads' },
  { value: 100, label: '100 leads' },
  { value: 'custom', label: 'Custom amount' },
];

export function CampaignsPage() {
  const navigate = useNavigate();
  const { user, userData } = useAuth();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    niche: 'any',
    country: DEFAULT_CAMPAIGN_COUNTRY,
    maxLeads: 25,
  });
  const [isCustomLeads, setIsCustomLeads] = useState(false);
  const [customLeadsValue, setCustomLeadsValue] = useState('');
  const [isLocalCampaign, setIsLocalCampaign] = useState(false);
  const [localCity, setLocalCity] = useState('');
  const [citySuggestions, setCitySuggestions] = useState<Array<{ name: string; fullName: string }>>([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [scrapingId, setScrapingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [showHackerScreen, setShowHackerScreen] = useState(false);
  const [scrapingLogs, setScrapingLogs] = useState<string[]>([]);
  const [scrapingStats, setScrapingStats] = useState({
    businessesFound: 0,
    businessesWithEmail: 0,
    businessesNoEmail: 0,
    citiesSearched: 0,
    progress: 0,
  });
  const [currentQuery, setCurrentQuery] = useState<string>('');
  const [currentLocation, setCurrentLocation] = useState<string>('');
  const [foundLeads, setFoundLeads] = useState<Array<{
    name: string;
    rating?: number;
    reviewCount?: number;
    location?: string;
    hasEmail?: boolean;
    hasPhone?: boolean;
    imageUrl?: string;
  }>>([]);
  const [scrapingCampaignId, setScrapingCampaignId] = useState<string | null>(null);

  const { data: campaigns, isLoading, error } = useCampaigns();
  const addCampaign = useAddDocument('campaigns');
  const deleteCampaign = useDeleteDocument('campaigns');

  // City autocomplete for local campaigns, worldwide (Places Autocomplete via
  // a callable). Only the latest request's answer is applied.
  const citySearchSeq = useRef(0);
  const handleCitySearch = (query: string) => {
    setLocalCity(query);
    const seq = ++citySearchSeq.current;
    if (query.trim().length < 2) {
      setCitySuggestions([]);
      setShowCitySuggestions(false);
      return;
    }
    window.setTimeout(async () => {
      if (seq !== citySearchSeq.current) return;
      try {
        const { suggestions } = await cloudFunctions.suggestCities({ input: query.trim() });
        if (seq !== citySearchSeq.current) return;
        setCitySuggestions(suggestions.slice(0, 8));
        setShowCitySuggestions(suggestions.length > 0);
      } catch {
        if (seq === citySearchSeq.current) setShowCitySuggestions(false);
      }
    }, 250);
  };

  const selectCity = (city: { name: string; fullName: string }) => {
    setLocalCity(city.fullName);
    setShowCitySuggestions(false);
    setCitySuggestions([]);
  };

  // Listen to leads being created in real-time during scraping
  useEffect(() => {
    if (!showHackerScreen || !user?.uid || !scrapingCampaignId) return;

    // Subscribe to leads for this campaign, ordered by creation time
    const leadsQuery = query(
      collection(db, 'leads'),
      where('userId', '==', user.uid),
      where('campaignId', '==', scrapingCampaignId),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(leadsQuery, (snapshot) => {
      const leads = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          name: data.businessName,
          rating: data.rating,
          reviewCount: data.reviewCount,
          location: data.location,
          hasEmail: !!data.email,
          hasPhone: !!data.phone,
          imageUrl: data.imageUrl,
        };
      });

      setFoundLeads(leads.reverse()); // Show oldest first
      setScrapingStats(prev => ({
        ...prev,
        businessesFound: leads.length,
        businessesWithEmail: leads.filter(l => l.hasEmail).length,
        progress: Math.min(90, leads.length * 3),
      }));
    });

    return () => unsubscribe();
  }, [showHackerScreen, user?.uid, scrapingCampaignId]);

  const handleCreate = async () => {
    if (!newCampaign.name.trim()) {
      toast.error('Please enter a campaign name');
      return;
    }

    if (isLocalCampaign && !localCity.trim()) {
      toast.error('Please enter a city for your local campaign');
      return;
    }

    if (!isLocalCampaign && !newCampaign.country) {
      toast.error('Please choose a country for your campaign');
      return;
    }

    const nicheLabel = NICHES.find(n => n.value === newCampaign.niche)?.label || newCampaign.niche;
    const location = isLocalCampaign ? localCity.trim() : newCampaign.country;

    try {
      await addCampaign.mutateAsync({
        name: newCampaign.name.trim(),
        niche: newCampaign.niche,
        nicheLabel,
        location,
        isLocalCampaign,
        status: 'active',
        maxLeads: newCampaign.maxLeads,
      });
      logAnalyticsEvent('campaign_created', {
        niche: newCampaign.niche,
        is_local: isLocalCampaign,
        max_leads: newCampaign.maxLeads,
        plan_id: userData?.planId || 'unknown',
      });
      toast.success('Campaign created successfully');
      setIsCreateOpen(false);
      setNewCampaign({ name: '', niche: 'any', country: DEFAULT_CAMPAIGN_COUNTRY, maxLeads: 25 });
      setIsCustomLeads(false);
      setCustomLeadsValue('');
      setIsLocalCampaign(false);
      setLocalCity('');
    } catch (error) {
      console.error('Campaign creation error:', error);
      toast.error('Failed to create campaign');
    }
  };

  // Map niche codes to proper Google Places search terms
  const getNicheSearchTerm = (nicheCode: string, nicheLabel?: string): string => {
    // If 'any', let the backend pick a random niche
    if (nicheCode === 'any') return 'any';

    // Use nicheLabel if available, but clean it up for search
    if (nicheLabel) {
      // Remove special characters and simplify for search
      return nicheLabel
        .replace(/\s*\/\s*/g, ' ') // "HVAC / Air Conditioning" -> "HVAC Air Conditioning"
        .replace(/\s*\([^)]*\)\s*/g, '') // Remove parenthetical text like "(Random)"
        .trim();
    }

    // Fallback: look up from NICHES array
    const niche = NICHES.find(n => n.value === nicheCode);
    if (niche) {
      return niche.label
        .replace(/\s*\/\s*/g, ' ')
        .replace(/\s*\([^)]*\)\s*/g, '')
        .trim();
    }

    return nicheCode;
  };

  const handleScrape = async (campaignId: string, niche: string, nicheLabel: string | undefined, location: string, maxLeads: number, isLocal: boolean) => {
    // Check if user has reached leads quota
    const leadsUsed = userData?.usage?.leadsUsed || 0;
    const leadsLimit = userData?.quotas?.leadsLimit || 50;
    if (leadsUsed >= leadsLimit) {
      setShowQuotaModal(true);
      return;
    }
    logAnalyticsEvent('scrape_started', {
      campaign_id: campaignId,
      niche,
      max_leads: maxLeads,
      leads_used: leadsUsed,
      leads_remaining: leadsLimit - leadsUsed,
      plan_id: userData?.planId || 'unknown',
    });

    setScrapingId(campaignId);
    setScrapingCampaignId(campaignId);
    setShowHackerScreen(true);
    setScrapingLogs([]);
    setFoundLeads([]);
    setScrapingStats({
      businessesFound: 0,
      businessesWithEmail: 0,
      businessesNoEmail: 0,
      citiesSearched: 0,
      progress: 0,
    });

    // Set real query and location for display
    const locationLabel = isLocal ? location : countryName(location);
    const searchTerm = getNicheSearchTerm(niche, nicheLabel);
    setCurrentQuery(searchTerm === 'any' ? 'Random Business Types' : searchTerm);
    setCurrentLocation(locationLabel);

    try {
      const result = await cloudFunctions.scrapeGooglePlaces({
        campaignId,
        query: searchTerm,
        // Countries are sent as their stored code; the server resolves any
        // country (including legacy "UK") and discovers its towns.
        location,
        maxLeads,
        isLocalCampaign: isLocal,
      });

      // Update with final results
      setScrapingStats(prev => ({
        ...prev,
        businessesFound: result.leadsWithoutWebsite || result.leadsCreated,
        businessesWithEmail: result.leadsCreated,
        businessesNoEmail: result.leadsNoEmail || 0,
        progress: 100,
      }));

      if (result.leadsCreated > 0) {
        setScrapingLogs(prev => [
          ...prev,
          `✓ Searched for "${result.nicheSearched}" in ${locationLabel}`,
          `✓ Found ${result.leadsWithoutWebsite} businesses without websites`,
          `✓ ${result.leadsCreated} have valid contact info`,
          result.leadsNoEmail ? `⚠ ${result.leadsNoEmail} skipped (no contact info)` : '',
          `✓ Scraping complete! Redirecting...`,
        ].filter(Boolean));
      } else {
        setScrapingLogs(prev => [
          ...prev,
          `⚠ No businesses with contact info found for "${result.nicheSearched}"`,
          `✗ Try a different niche or location`,
        ].filter(Boolean));
      }

      // Wait a moment to show final results
      await new Promise(resolve => setTimeout(resolve, 2000));

      setShowHackerScreen(false);
      setScrapingCampaignId(null);

      if (result.leadsCreated === 0) {
        toast.error(`No businesses with contact info found for "${result.nicheSearched}". Try a different niche or location.`);
      } else {
        toast.success(`Found ${result.leadsCreated} businesses! Redirecting to Leads...`);
        navigate('/dashboard/leads');
      }
    } catch (error: unknown) {
      setScrapingLogs(prev => [...prev, `✗ Error: ${error instanceof Error ? error.message : 'Unknown error'}`]);
      await new Promise(resolve => setTimeout(resolve, 2000));
      setShowHackerScreen(false);
      setScrapingCampaignId(null);
      const message = error instanceof Error ? error.message : 'Failed to scrape leads';
      toast.error(message);
    } finally {
      setScrapingId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;
    try {
      await deleteCampaign.mutateAsync(deleteConfirmId);
      toast.success('Campaign deleted');
    } catch (error) {
      toast.error('Failed to delete campaign');
    } finally {
      setDeleteConfirmId(null);
    }
  };

  const getLocationDisplay = (location: string, isLocal?: boolean) =>
    isLocal ? `📍 ${location}` : `${countryFlag(location)} ${countryName(location)}`;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-snow uppercase tracking-widest">Campaigns</h1>
          <p className="text-gray-400 mt-1">Manage your lead scraping campaigns</p>
        </div>
        <Button onClick={() => setIsCreateOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          New Campaign
        </Button>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <GlassCard key={i} className="animate-pulse">
              <div className="h-6 bg-white/10 rounded w-3/4 mb-4" />
              <div className="h-4 bg-white/10 rounded w-1/2 mb-2" />
              <div className="h-4 bg-white/10 rounded w-2/3" />
            </GlassCard>
          ))}
        </div>
      ) : error ? (
        <GlassCard className="text-center py-12">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <Search className="w-8 h-8 text-red-400" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">Failed to load campaigns</h3>
          <p className="text-gray-400 mb-6">
            {error instanceof Error ? error.message : 'Please check your Firestore rules and try again.'}
          </p>
        </GlassCard>
      ) : campaigns && campaigns.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {campaigns.map((campaign) => (
            <GlassCard key={campaign.id} hover>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-white">{campaign.name}</h3>
                  <p className="text-sm text-gray-500">
                    {campaign.nicheLabel || NICHES.find(n => n.value === campaign.niche)?.label || campaign.niche}
                  </p>
                </div>
                <Badge
                  variant={campaign.status === 'active' ? 'success' : campaign.status === 'paused' ? 'warning' : 'secondary'}
                >
                  {campaign.status}
                </Badge>
              </div>

              <div className="space-y-2 text-sm text-gray-400 mb-6">
                <p className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  {getLocationDisplay(campaign.location, campaign.isLocalCampaign)}
                </p>
                <p className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Max {campaign.maxLeads || 25} leads per scrape
                </p>
                {campaign.lastScrapedAt && (
                  <p>Last scraped: {formatDate(campaign.lastScrapedAt)}</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                {campaign.lastScrapedAt ? (
                  <div className="flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-green-500/10 border border-green-500/20 text-green-400 text-sm font-medium">
                    <CheckCircle className="w-4 h-4" />
                    Scraped
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    loading={scrapingId === campaign.id}
                    onClick={() => handleScrape(campaign.id!, campaign.niche, campaign.nicheLabel, campaign.location, campaign.maxLeads || 25, !!campaign.isLocalCampaign)}
                  >
                    <Search className="w-4 h-4 mr-1" />
                    Scrape Leads
                  </Button>
                )}
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setDeleteConfirmId(campaign.id!)}
                >
                  <Trash2 className="w-4 h-4 text-red-400" />
                </Button>
              </div>
            </GlassCard>
          ))}
        </div>
      ) : (
        <GlassCard className="text-center py-12">
          <div className="w-16 h-16 rounded-full bg-primary-500/10 flex items-center justify-center mx-auto mb-4">
            <Search className="w-8 h-8 text-primary-400" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">No campaigns yet</h3>
          <p className="text-gray-400 mb-6">Create your first campaign to start finding leads</p>
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Campaign
          </Button>
        </GlassCard>
      )}

      <Dialog open={isCreateOpen} onOpenChange={(open) => {
        setIsCreateOpen(open);
        if (!open) {
          setIsCustomLeads(false);
          setCustomLeadsValue('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Campaign</DialogTitle>
            <DialogDescription>
              Set up a new campaign to scrape leads from Google Places
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Campaign Name</Label>
              <Input
                id="name"
                placeholder="e.g., US Plumbers Campaign"
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="niche">Business Niche</Label>
              <Select
                value={newCampaign.niche}
                onValueChange={(value) => setNewCampaign({ ...newCampaign, niche: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NICHES.map((niche) => (
                    <SelectItem key={niche.value} value={niche.value}>
                      {niche.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">Select "Any Niche" for random businesses without websites</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="country">Target Location</Label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-xs text-gray-400">Local Campaign</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isLocalCampaign}
                    onClick={() => {
                      setIsLocalCampaign(!isLocalCampaign);
                      if (!isLocalCampaign) {
                        setLocalCity('');
                      }
                    }}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      isLocalCampaign ? 'bg-primary-500' : 'bg-white/20'
                    }`}
                  >
                    <span
                      className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                        isLocalCampaign ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </label>
              </div>

              {isLocalCampaign ? (
                <div className="relative">
                  <Input
                    placeholder="Start typing a city (e.g., Lyon, France)"
                    value={localCity}
                    onChange={(e) => handleCitySearch(e.target.value)}
                    onFocus={() => localCity.length >= 2 && citySuggestions.length > 0 && setShowCitySuggestions(true)}
                    onBlur={() => setTimeout(() => setShowCitySuggestions(false), 200)}
                  />
                  {showCitySuggestions && citySuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-[#0a0a0a] border border-rose/20 rounded-lg shadow-xl overflow-hidden">
                      {citySuggestions.map((city, i) => (
                        <button
                          key={i}
                          type="button"
                          className="w-full px-4 py-2 text-left text-sm text-white hover:bg-primary-500/20 transition-colors"
                          onMouseDown={() => selectCity(city)}
                        >
                          📍 {city.fullName}
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Search for a city anywhere in the world</p>
                </div>
              ) : (
                <Select
                  value={newCampaign.country}
                  onValueChange={(value) => setNewCampaign({ ...newCampaign, country: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a country" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {COUNTRY_OPTIONS.map((country) => (
                      <SelectItem key={country.code} value={country.code}>
                        {country.flag} {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="maxLeads">Maximum Leads per Scrape</Label>
              {isCustomLeads ? (
                <div className="flex gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={500}
                    placeholder="Enter amount (1-500)"
                    value={customLeadsValue}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCustomLeadsValue(val);
                      const num = parseInt(val);
                      if (!isNaN(num) && num >= 1 && num <= 500) {
                        setNewCampaign({ ...newCampaign, maxLeads: num });
                      }
                    }}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setIsCustomLeads(false);
                      setCustomLeadsValue('');
                      setNewCampaign({ ...newCampaign, maxLeads: 25 });
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <Select
                  value={newCampaign.maxLeads.toString()}
                  onValueChange={(value) => {
                    if (value === 'custom') {
                      setIsCustomLeads(true);
                      setCustomLeadsValue('');
                    } else {
                      setNewCampaign({ ...newCampaign, maxLeads: parseInt(value) });
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MAX_LEADS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value.toString()}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} loading={addCampaign.isPending}>
              Create Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hacker-style loading screen for scraping */}
      <HackerLoadingScreen
        isOpen={showHackerScreen}
        title="SCRAPING LEADS"
        subtitle="Finding businesses without websites & enriching contact info"
        mode="scraping"
        logs={scrapingLogs}
        stats={scrapingStats}
        currentQuery={currentQuery}
        currentLocation={currentLocation}
        foundLeads={foundLeads}
      />

      {/* Quota limit modal */}
      <QuotaLimitModal
        isOpen={showQuotaModal}
        onClose={() => setShowQuotaModal(false)}
        type="leads"
        currentUsage={userData?.usage?.leadsUsed || 0}
        limit={userData?.quotas?.leadsLimit || 50}
        planId={userData?.planId}
      />

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Campaign</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this campaign? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
